/**
 * Arredonda um número conforme ABNT NBR 5891
 * https://i3sistema.com.br/crm/knowledge-base/article/regras-de-arredondamento-na-numera-ao-decimal-norma-abnt-nbr-5891
 *
 * Se o primeiro dígito descartado for:
 * - Menor que 5 → Mantém o último dígito que fica (não arredonda).
 * - Maior que 5 → Aumenta o último dígito em 1 (arredonda para cima).
 * - Igual a 5 → OLHAR o número anterior:
 *   - Se o número anterior for ÍMPAR → Arredonda para cima.
 *   - Se o número anterior for PAR → Mantém o número (não arredonda).
 */
export function roundAbnt(valor: number, casasDecimais: number = 2): number {
    if (Number.isNaN(valor) || !Number.isFinite(valor)) {
        return valor;
    }

    const multiplicador = 10 ** casasDecimais;
    const valorMultiplicado = valor * multiplicador;

    // Separa a parte inteira da parte decimal
    const parteInteira = Math.floor(valorMultiplicado);
    const parteDecimal = valorMultiplicado - parteInteira;

    // Se não há parte decimal significativa, retorna o valor já arredondado
    if (parteDecimal < 0.0000000001) {
        return parteInteira / multiplicador;
    }

    // Primeiro dígito descartado
    const primeiroDigitoDescartado = Math.floor(parteDecimal * 10);

    let resultado: number;

    if (primeiroDigitoDescartado < 5) {
        // Menor que 5: mantém o último dígito
        resultado = parteInteira;
    } else if (primeiroDigitoDescartado > 5) {
        // Maior que 5: arredonda para cima
        resultado = parteInteira + 1;
    } else {
        // Igual a 5: verifica se o número anterior é par ou ímpar
        const restoDigitosDescartados = parteDecimal * 10 - primeiroDigitoDescartado;

        // Tolerância para erros de ponto flutuante (ignora diferenças menores que 0.00001)
        const tolerancia = 0.00001;

        // Se há outros dígitos significativos após o 5, arredonda para cima
        if (restoDigitosDescartados > tolerancia) {
            resultado = parteInteira + 1;
        } else {
            // Exatamente 5 (ou muito próximo devido a imprecisão de ponto flutuante):
            // verifica se o último dígito é par ou ímpar
            if (parteInteira % 2 === 0) {
                // Par: mantém
                resultado = parteInteira;
            } else {
                // Ímpar: arredonda para cima
                resultado = parteInteira + 1;
            }
        }
    }

    return resultado / multiplicador;
}

/**
 * Arredonda todos os valores numéricos de um objeto conforme ABNT NBR 5891.
 * ATENÇÃO: Esta função modifica o objeto original (mutação in-place).
 *
 * @param obj - Objeto cujos valores serão arredondados
 * @param decimalPlaces - Número de casas decimais
 */
export function roundAbntObject<T extends Record<string, number>>(
    obj: T,
    decimalPlaces: number,
): void {
    for (const key in obj) {
        if (Object.hasOwn(obj, key)) {
            const value = obj[key] as number;
            obj[key] = roundAbnt(value, decimalPlaces) as T[Extract<keyof T, string>];
        }
    }
}

export function truncateDecimal(value: number, decimalPlaces: number): number {
    const power = 10 ** decimalPlaces;
    return Math.round(value * power) / power;
}
